import { motion, AnimatePresence } from "framer-motion"
import { useWizard } from "./WizardContext"
import { cn } from "@/lib/utils"
import {
  MapPin,
  Ruler,
  LayoutGrid,
  Settings2,
  Clock,
  CreditCard,
  User,
  FileCheck,
} from "lucide-react"
import AddressStep from "./AddressStep"
import RoofDrawingStep from "./RoofDrawingStep"
import ComplexityStep from "./ComplexityStep"
import FeaturesStep from "./FeaturesStep"
import TimelineStep from "./TimelineStep"
import PaymentStep from "./PaymentStep"
import ContactStep from "./ContactStep"
import ResultsStep from "./ResultsStep"

const steps = [
  { label: "Address", icon: MapPin },
  { label: "Roof", icon: Ruler },
  { label: "Style", icon: LayoutGrid },
  { label: "Features", icon: Settings2 },
  { label: "Timeline", icon: Clock },
  { label: "Payment", icon: CreditCard },
  { label: "Contact", icon: User },
  { label: "Estimate", icon: FileCheck },
]

const stepComponents = [
  AddressStep,
  RoofDrawingStep,
  ComplexityStep,
  FeaturesStep,
  TimelineStep,
  PaymentStep,
  ContactStep,
  ResultsStep,
]

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 80 : -80,
    opacity: 0,
  }),
}

export default function WizardShell() {
  const { state } = useWizard()
  const currentStep = state.step
  const StepComponent = stepComponents[currentStep - 1]
  const isResults = currentStep === 8

  return (
    <div className="min-h-[600px] flex flex-col">
      {/* Progress indicator */}
      {!isResults && (
        <div className="mb-8">
          {/* Step icons row */}
          <div className="flex items-center justify-between mb-3 px-1">
            {steps.map((step, i) => {
              const stepNum = i + 1
              const isActive = stepNum === currentStep
              const isCompleted = stepNum < currentStep
              const Icon = step.icon

              return (
                <div
                  key={step.label}
                  className={cn(
                    "flex flex-col items-center gap-1.5 transition-all duration-300",
                    isActive && "scale-110",
                  )}
                >
                  <div
                    className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300",
                      isCompleted && "bg-primary text-white",
                      isActive && "bg-primary text-white ring-4 ring-primary/20",
                      !isActive && !isCompleted && "bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span
                    className={cn(
                      "text-[10px] font-semibold uppercase tracking-wider transition-colors",
                      isActive ? "text-primary" : isCompleted ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-secondary rounded-full"
              initial={false}
              animate={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait" custom={1}>
          <motion.div
            key={currentStep}
            custom={1}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <StepComponent />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
